import type { z } from "zod";
import type { Settings } from "./settings";

/** A typed, user-readable API failure. `status` is the HTTP code (0 = network). */
export class ApiError extends Error {
  status: number;
  retryAfter?: number;
  constructor(status: number, message: string, retryAfter?: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.retryAfter = retryAfter;
  }
}

// Friendly, action-oriented copy per status (web/design.md §8). Falls back to
// the server's `{error}` text when present, then to a generic line.
function friendly(status: number, serverError?: string): string {
  switch (status) {
    case 0:
      return "Cannot reach the server — check the API base and your network";
    case 401:
    case 403:
      return serverError || "Session expired or credentials invalid — please sign in again";
    case 402:
      return serverError || "Insufficient credits — please buy more first";
    case 404:
      return serverError || "Not found";
    case 429:
    case 503:
      return serverError || "Service busy, please retry shortly";
    default:
      return serverError || `Request failed (${status})`;
  }
}

export interface RequestOptions {
  method?: "GET" | "POST" | "DELETE";
  body?: unknown;
  /**
   * A STABLE Idempotency-Key for this logical operation. The same key MUST be
   * reused across every retry of the same user intent (e.g. re-clicking after a
   * network error) so the server collapses duplicates instead of double-charging
   * or double-ordering. Generate it once per intent — never per request.
   */
  idempotencyKey?: string;
  /** Extra request headers (e.g. X-Progress-Id for the live progress channel). */
  headers?: Record<string, string>;
  /** Abort a previous in-flight humanize when the user starts a new one. */
  signal?: AbortSignal;
}

/** UUID for idempotency / progress. `randomUUID` is HTTPS-only; this page is HTTP. */
export function newIdempotencyKey(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") {
    return c.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === "function") {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

const trimBase = (b: string) => b.replace(/\/+$/, "");

function buildHeaders(settings: Settings, opts: RequestOptions): Record<string, string> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(opts.headers ?? {}),
  };
  // Auth precedence mirrors the server (AuthAccount): a Web session JWT wins;
  // otherwise fall back to the developer API key.
  if (settings.jwt) headers["Authorization"] = `Bearer ${settings.jwt}`;
  else if (settings.apiKey) headers["X-API-Key"] = settings.apiKey;
  if (opts.idempotencyKey) headers["Idempotency-Key"] = opts.idempotencyKey;
  return headers;
}

async function throwApiError(res: Response): Promise<never> {
  const text = await res.text();
  let serverError: string | undefined;
  try {
    const parsed: unknown = text ? JSON.parse(text) : undefined;
    if (parsed && typeof parsed === "object" && "error" in parsed) {
      serverError = String((parsed as { error: unknown }).error);
    }
  } catch {
    serverError = text || undefined;
  }
  const ra = Number(res.headers.get("Retry-After"));
  throw new ApiError(
    res.status,
    friendly(res.status, serverError),
    Number.isFinite(ra) && ra > 0 ? ra : undefined,
  );
}

/**
 * Streaming POST consuming an SSE body (`data: {json}\n\n` frames). Each
 * parsed frame is handed to `onEvent`; the promise resolves when the server
 * closes the stream. Non-2xx responses throw the same {@link ApiError} as
 * {@link request}.
 */
export async function requestStream(
  settings: Settings,
  path: string,
  opts: RequestOptions,
  onEvent: (data: unknown) => void,
): Promise<void> {
  const url = trimBase(settings.apiBase) + path;
  let res: Response;
  try {
    res = await fetch(url, {
      method: opts.method ?? "POST",
      headers: buildHeaders(settings, opts),
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  } catch {
    throw new ApiError(0, friendly(0));
  }
  if (!res.ok) await throwApiError(res);
  if (!res.body) throw new ApiError(res.status, "The server returned no data stream");

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx = buf.indexOf("\n\n");
    while (idx >= 0) {
      const frame = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      idx = buf.indexOf("\n\n");
      const data = frame
        .split("\n")
        .filter((l) => l.startsWith("data:"))
        .map((l) => l.slice(5).replace(/^ /, ""))
        .join("\n");
      if (!data) continue; // keep-alive comment frames
      try {
        onEvent(JSON.parse(data));
      } catch {
        // Malformed frame: skip rather than kill the whole stream.
      }
    }
  }
}

/**
 * Core request: attaches auth + idempotency headers, maps non-2xx to a readable
 * {@link ApiError}, and validates the body against a zod schema when given.
 */
export async function request<S extends z.ZodTypeAny>(
  settings: Settings,
  path: string,
  schema: S,
  opts: RequestOptions = {},
): Promise<z.infer<S>> {
  const url = trimBase(settings.apiBase) + path;

  let res: Response;
  try {
    res = await fetch(url, {
      method: opts.method ?? "GET",
      headers: buildHeaders(settings, opts),
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });
  } catch {
    throw new ApiError(0, friendly(0));
  }

  if (!res.ok) await throwApiError(res);

  const text = await res.text();
  let parsed: unknown = undefined;
  try {
    parsed = text ? JSON.parse(text) : undefined;
  } catch {
    parsed = { error: text };
  }

  const result = schema.safeParse(parsed);
  if (!result.success) {
    throw new ApiError(res.status, "The server returned an unrecognized data format");
  }
  return result.data;
}
